/**
 * A3 Baseline / A11 Renko / A11 Kagi — render-mode SSOT.
 *
 * `KernelEngine.resolveSeriesMode` used to return only `"line" | "area" | "bar"`,
 * which is why a registered-but-unrecognised chart type silently rendered as a
 * Line: there was no mode that could express "rectangles between a brick open
 * and a brick close" or "a step curve", so every exotic type collapsed onto the
 * scalar line path. This module is the single place that decides which geometry
 * a series type gets; `KernelEngine` only asks the predicates below.
 *
 * Geometry per mode:
 *  - `baseline` — polyline through `datum.normalizedPrices[PRIMARY_BASELINE_KEY]`,
 *    i.e. percent change from the anchor bar. Percent, not absolute: the only
 *    normalized-price writer in the codebase emits percent
 *    (`normalizePrices.ts` `((close - base) / base) * 100`) and every consumer
 *    of that map already treats it as percent
 *    (`seriesValueResolver.ts` overlay read, and this package's own
 *    `valueMode: "percent"` overlay branch). So a Baseline chart is anchored at
 *    0 and its Y axis is a percentage axis.
 *  - `renko` — one rectangle per bar, from `datum.renkoOpen` to
 *    `datum.renkoClose`. Both endpoints are per-datum because a renko brick is
 *    not a price bar; reusing the bar's own OHLC would corrupt every other
 *    consumer of the same datum.
 *  - `kagi` — polyline through `datum.current` (the live kagi price level) drawn
 *    with `stepBefore` interpolation, because the level is constant between
 *    reversals and a straight segment would draw a slope that does not exist.
 *
 *  - `pointandfigure` — one rectangle per BOX, with every box of a column forced
 *    onto the column's own x. This is why it is its own mode and not an alias of
 *    `renko`: `toBarPoints` gives every BAR its own x, so aliasing would smear a
 *    column across N bar slots. See `pointAndFigureColumns.ts`.
 *  - `linebreak` — polyline through `datum.lineBreakClose` (the end level of the
 *    line being drawn) with `stepBefore`, for the same reason as kagi: the level
 *    is constant between line ends, so a straight segment would draw a slope
 *    that does not exist. The SEGMENTATION rule differs from kagi (line break
 *    uses one `n` for both the step and the reversal), but the geometry a
 *    renderer has to draw does not, so the two share the step predicate.
 */
/** Reserved `normalizedPrices` key holding the PRIMARY symbol's percent-from-anchor. */
export const PRIMARY_BASELINE_KEY = "__primary__";
/** Series types that must be resolved by name, not by the substring heuristics below. */
const EXOTIC_MODE_BY_TYPE = {
    baseline: "baseline",
    renko: "renko",
    kagi: "kagi",
    pointandfigure: "pointandfigure",
    linebreak: "linebreak",
};
/**
 * Resolve the render mode for a series.
 *
 * Exotic types are matched on the exact lower-cased type name so they can never
 * be swallowed by the substring heuristics (`"bar"` matches "Histogram",
 * `"volume"` matches "Volume", …). Everything else keeps the pre-existing
 * behaviour unchanged.
 */
export function resolveSeriesRenderMode(series) {
    const type = series.type.toLowerCase();
    const exotic = EXOTIC_MODE_BY_TYPE[type];
    if (exotic) {
        return exotic;
    }
    if (type === "area") {
        return "area";
    }
    if (type.includes("volume")
        || type.includes("bar")
        || type.includes("histogram")
        || type === "macd"
        || type === "whale"
        || type.includes("cvd")) {
        return "bar";
    }
    const asArea = typeof series.style?.["asArea"] === "boolean"
        ? Boolean(series.style["asArea"])
        : false;
    return asArea ? "area" : "line";
}
/**
 * True for modes whose bars are measured from zero, so the value domain must
 * include a zero floor. Deliberately EXCLUDES `renko`: a renko brick is measured
 * from the previous brick, not from zero, and a zero-floored domain would
 * collapse the whole chart into the top of the pane.
 */
export function isZeroFlooredMode(mode) {
    return mode === "bar";
}
/**
 * True for modes drawn through `BarRenderer` — one rectangle per bar. `"bar"`
 * is the pre-existing zero-based mode (volume, histogram, MACD…); `"renko"`
 * joins it with a per-bar baseline instead of zero.
 *
 * `"pointandfigure"` is deliberately NOT here even though it also draws
 * rectangles: it needs one rectangle per BOX with a per-COLUMN x, which
 * `toBarPoints` cannot express. `isColumnStackMode` is the predicate for it.
 */
export function isRectangleMode(mode) {
    return mode === "bar" || mode === "renko";
}
/** True only for the zero-based rectangle mode; renko measures from the brick open. */
export function isRenkoMode(mode) {
    return mode === "renko";
}
/**
 * True for Point & Figure: rectangles whose x comes from the COLUMN, not the bar.
 *
 * This predicate gates two things together, and they must not be split: the
 * geometry (`pointAndFigureColumns.layoutPointAndFigure`) and the value domain
 * (`pointAndFigureColumns.widenDomainForPointAndFigure`). A P&F box spans one
 * reversal unit, and the scalar the accessor reports is only the column's leading
 * edge — so a domain derived from those scalars alone is narrower than the drawn
 * geometry and clips the bottom of every column.
 */
export function isColumnStackMode(mode) {
    return mode === "pointandfigure";
}
/**
 * True for modes whose polyline must use step interpolation.
 *
 * Both members draw a level that is CONSTANT until the current segment ends, so
 * a straight segment would draw a slope between two values that never coexisted.
 */
export function isStepMode(mode) {
    return mode === "kagi" || mode === "linebreak";
}
/**
 * True for the non-exotic modes. The price line + last-value badge are drawn
 * from `lastBar.close`; for an exotic price series the plotted value is NOT the
 * bar close (a renko brick close sits on the brick ladder, a kagi level is a
 * reversal level, a line-break level is a line end, a baseline value is a
 * percentage), so the badge would be drawn at a y that belongs to no data point.
 */
export function isPriceLevelMode(mode) {
    return !isStepMode(mode) && mode !== "baseline" && !isRenkoMode(mode) && !isColumnStackMode(mode);
}
/** Renko colours — identical to `RenkoSeries.defaultProps.fill` in the host repo. */
const RENKO_UP = "#6BA583";
const RENKO_DOWN = "#E60000";
const RENKO_PARTIAL = "#4682B4";
/**
 * Per-bar baseline for rectangle modes, or `undefined` when the mode does not
 * need one. `toBarPoints` skips bars whose baseline is not finite, so a bar with
 * no brick simply is not drawn — it is never drawn from zero.
 */
export function resolveRectangleBaselines(enrichedData, fromIndex, count) {
    const baselines = new Array(count);
    for (let i = 0; i < count; i++) {
        const open = enrichedData[fromIndex + i]?.["renkoOpen"];
        baselines[i] = typeof open === "number" && Number.isFinite(open) ? open : undefined;
    }
    return baselines;
}
/** Per-bar fill for rectangle modes: brick direction, or the "partial" colour. */
export function resolveRectanglePalette(enrichedData, fromIndex, count) {
    const palette = new Array(count);
    for (let i = 0; i < count; i++) {
        const datum = enrichedData[fromIndex + i];
        const open = datum?.["renkoOpen"];
        if (typeof open !== "number" || !Number.isFinite(open)) {
            palette[i] = undefined;
            continue;
        }
        // An unfinished brick (price has not moved a whole brick since the last
        // one) is drawn in the neutral colour so it cannot be read as a close.
        palette[i] = datum?.["fullyFormed"] === false
            ? RENKO_PARTIAL
            : datum?.["renkoUp"] === true
                ? RENKO_UP
                : RENKO_DOWN;
    }
    return palette;
}
